<!--eslint-disable vue/multi-word-component-names-->
<template>
  <div class="category-page">
    <div
      v-if="showInfoLoader && !categoryInfo"
      class="category-page__loading--info"
    />
    <CategoryHeader
      v-else-if="!showInfoLoader && categoryInfo"
      :category-info="categoryInfo"
    />
    <CategoryListSlider
      v-if="
        categoriesList &&
        Array.isArray(categoriesList) &&
        categoriesList.length > 0
      "
      :categories-list="categoriesList"
      :current-category-id="categoryGrn"
    />
    <CategoryFilters
      v-if="result"
      :loading="loading"
      :result="result"
      :search-specs-str="searchSpecsString"
      :category-id="id"
      :products-categories-names="productsCategoriesNames"
    />
    <div v-if="showProductsLoader" class="category-page__loading">
      <LoadingDots />
    </div>
    <ProductList
      :products="products"
      v-else-if="!showProductsLoader && products.length > 0"
    />
    <div v-else class="category-page__no-products">
      <CustomCta
        class="category-page__no-products__reset-filters"
        v-if="showResetFilters"
        @click="showResetFilters({})"
      >
        {{ $t('Reset your filters') }}
      </CustomCta>
      <h3 class="category-page__no-products__heading">
        {{ $t('No products were found') }}
      </h3>
    </div>
    <CategoryPagination
      :is-loading-more-products="isLoadingMoreProducts"
      :page-size="pageSize"
      :current-page="page"
      :category-products-count="categoryProductsCount"
      :is-last-page="isLastPage"
      :products-currently-shown-in-page="productsCurrentlyShownInPage"
      @loadMore="searchMoreProduct"
      @resetPageFilter="resetPageFilter"
    />
    <StructuredBreadcrumbData />
  </div>
</template>

<script>
import {
  defineComponent,
  useRoute,
  useFetch,
  reactive,
  toRefs,
  computed,
  ref,
  useMeta,
} from '@nuxtjs/composition-api';
import {
  facetGetters,
  useFacet,
  useCategorySearch,
} from '@gemini-vsf/composables';
import { useUiHelpers } from '~/composables';
import { useCache, CacheTagPrefix } from '@gemini/vsf-cache';
import cacheControl from '~/helpers/cacheControl';
import ProductList from '~/components/Category/ProductList';
import LoadingDots from '~/components/General/LoadingDots.vue';
import CategoryHeader from '~/components/Category/CategoryHeader.vue';
import CategoryListSlider from '~/components/Category/CategoryListSlider.vue';
import CategoryPagination from '~/components/Category/CategoryPagination.vue';
import CategoryFilters from '~/components/Category/CategoryFilters.vue';
import { useGtm } from '~/composables';
import { CustomCta } from '~/components/General';
import { useGetAlternateMatching } from '~/helpers/alternate/getAlternateMatching';
import { handleSeo } from '~/helpers/seo/handleSeo';
import useGeminiApi from '~/composables/useGeminiApi/useGeminiApi';
import StructuredBreadcrumbData from '~/components/StructuredData/StructuredBreadcrumbData.vue';

export default defineComponent({
  name: 'CategoryPage',
  components: {
    ProductList,
    LoadingDots,
    CategoryHeader,
    CategoryListSlider,
    CategoryPagination,
    CategoryFilters,
    CustomCta,
    StructuredBreadcrumbData,
  },
  middleware: cacheControl({
    'max-age': 60,
    'stale-when-revalidate': 5,
  }),
  transition: 'fade',
  props: {
    categoryId: {
      type: String,
      default: '',
    },
    routerFetching: {
      type: Boolean,
      default: true,
    },
  },
  head: {},
  setup(props) {
    const route = useRoute();
    const id = props?.categoryId || route?.value?.params?.categoryId || '';
    const seoAlternateurls = ref([]);
    const alternate = computed(() => seoAlternateurls.value);
    const { getGeminiData, geminiData } = useGeminiApi();
    const { getAlternateArray } = useGetAlternateMatching();
    const { getCanonical } = handleSeo();
    const canonicalLink = computed(() => getCanonical());

    const getGeminiDataFunction = async () => {
      try {
        await getGeminiData(`grn:product-list:product-list::${id}`);
        seoAlternateurls.value = getAlternateArray(geminiData.value);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return JSON.parse(JSON.stringify(seoAlternateurls.value));
      } catch (error) {
        console.error('Category ~ getGeminiDataFunction ~ error:', error);
      }
    };

    const uiHelpers = useUiHelpers();
    const categoryPageState = reactive({
      page: uiHelpers?.getFacetsFromURL().page || 1,
      isLoadingMoreProducts: false,
      showProductsLoader: true,
      showInfoLoader: true,
      categoryInfo: null,
      products: [],
      categoriesList: [],
      pageSize: 36,
      categoryProductsCount: null,
    });
    const { pageGtmPush, categoryGtmPush } = useGtm();
    const showResetFilters = computed(
      () =>
        Object.keys(uiHelpers?.getFacetsFromURL().filters).length > 0 &&
        categoryPageState.categoryProductsCount === 0 &&
        uiHelpers.changeFilters
    );

    const filtersString = JSON.stringify(uiHelpers.getFacetsFromURL());
    const searchSpecsString = `facetId:${id}:${filtersString}`;
    const { result, search, loading } = useFacet(searchSpecsString);
    const { addTags } = useCache();

    const { result: categorySearchResult, search: categorySearch } =
      useCategorySearch();

    const isLastPage = computed(() => result?.value?.data?.isLastPage || false);
    const productsCurrentlyShownInPage = computed(
      () => categoryPageState?.products?.length || categoryPageState.pageSize
    );

    const categoryGrn = `grn:product-list:product-list::${id}`;

    const productsCategoriesNames = ref({});

    const showFilters = ref(false);

    const fetchInitialResults = async (goToFirstPage = false) => {
      const searchParams = {
        ...uiHelpers.getFacetsFromURL(),
        categoryId: categoryGrn,
        itemsPerPage: categoryPageState.pageSize,
        customQuery: {
          products: 'productListMinimal',
        },
      };
      if (goToFirstPage) searchParams.page = 1;
      await search(searchParams);

      categoryPageState.products = facetGetters
        .getProducts(result.value)
        .filter(Boolean);
      // Crate an object with uid = name for every category listed in all products
      categoryPageState.products.forEach((product) => {
        product?.categories.forEach((category) => {
          if (
            category?.name &&
            !Object.hasOwn(productsCategoriesNames.value, [
              category.uid.split('::')[1],
            ])
          ) {
            productsCategoriesNames.value = {
              ...productsCategoriesNames.value,
              [category.uid.split('::')[1]]: category?.name,
            };
          }
        });
      });
      showFilters.value = true;

      categoryPageState.categoryProductsCount = result.value.data.total;
      categoryPageState.showProductsLoader = false;
    };

    const fetchCategoryInfo = async () => {
      await categorySearch({
        customQuery: {
          categorySearch: 'categoryListCustom',
        },
      });
      categoryPageState.categoryInfo = categorySearchResult.value.find(
        (e) => e.uid === categoryGrn
      );
      const categoriesToConsider =
        categoryPageState.categoryInfo.url_key.split('/');

      categoryPageState.categoriesList = categorySearchResult.value.filter(
        (c) =>
          c.url_key.includes(
            categoriesToConsider[
              categoriesToConsider.length > 1
                ? categoriesToConsider.length - 2
                : 0
            ]
          )
      );
      categoryPageState.showInfoLoader = false;
      pageGtmPush(
        categoryPageState.categoryInfo?.name ||
          categoryPageState.categoryInfo?.meta_title ||
          categoryPageState.categoryInfo?.url_key ||
          'Category'
      );
      categoryGtmPush(
        categoryPageState.products,
        categoryPageState.categoryInfo?.name
      );
    };

    useFetch(async () => {
      await fetchInitialResults();
      await fetchCategoryInfo();
      await getGeminiDataFunction();
      addTags([
        { prefix: CacheTagPrefix.View, value: 'category' },
        { prefix: CacheTagPrefix.Category, value: id },
      ]);
    });

    const searchMoreProduct = async () => {
      if (result?.value?.data?.isLastPage) {
        return;
      }

      categoryPageState.page += 1;
      categoryPageState.isLoadingMoreProducts = true;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      await search({
        ...uiHelpers.getFacetsFromURL(),
        categoryId: categoryGrn,
        page: categoryPageState.page,
        itemsPerPage: categoryPageState.pageSize,
        customQuery: {
          products: 'productListMinimal',
        },
      });

      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('page', categoryPageState.page.toString());

      history.pushState(
        {},
        null,
        `${route.value.path}?${queryParams.toString()}`
      );

      categoryPageState.products = [
        ...categoryPageState.products,
        ...facetGetters.getProducts(result.value),
      ];

      categoryPageState.isLoadingMoreProducts = false;
    };

    const resetPageFilter = async () => {
      categoryPageState.page = 1;
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('page');
      history.pushState(
        {},
        null,
        queryParams.toString().length > 0
          ? `${route.value.path}?${queryParams.toString()}`
          : route.value.path
      );

      categoryPageState.products = [];
      categoryPageState.showProductsLoader = true;
      await fetchInitialResults(true);
    };

    const TITLE_DEFAULT = 'Sogni Di Cristallo®';

    const metatitle = computed(() => {
      if (categoryPageState?.categoryInfo?.meta_title) {
        return `${categoryPageState?.categoryInfo?.meta_title} - ${TITLE_DEFAULT}`;
      }
      if (categoryPageState?.categoryInfo?.name) {
        return `${categoryPageState?.categoryInfo?.name} - ${TITLE_DEFAULT}`;
      }
      if (categoryPageState?.categoryInfo?.url_key) {
        return `${categoryPageState?.categoryInfo?.url_key.replace(
          '-',
          ' '
        )} - ${TITLE_DEFAULT}`;
      }
      return TITLE_DEFAULT;
    });

    useMeta(() => ({
      title: metatitle.value,
      meta: [
        {
          hid: 'title',
          name: 'title',
          content: metatitle.value,
        },
        {
          hid: 'og:title',
          name: 'og:title',
          content:
            categoryPageState?.categoryInfo?.meta_title ||
            categoryPageState?.categoryInfo?.name ||
            categoryPageState?.categoryInfo?.url_key?.replace('-', ' ') ||
            TITLE_DEFAULT,
        },
        {
          hid: 'description',
          name: 'description',
          content: categoryPageState?.categoryInfo?.meta_description || '',
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: categoryPageState?.categoryInfo?.meta_description || '',
        },
        //og:url
        {
          hid: 'og:url',
          property: 'og:url',
          content: canonicalLink.value?.href,
        },
        { hid: 'fb:app_id', property: 'fb:app_id', content: '208478719244524' },
      ],
      link: [...alternate.value, canonicalLink.value],
    }));

    return {
      searchMoreProduct,
      id,
      ...toRefs(categoryPageState),
      categoryGrn,
      isLastPage,
      productsCurrentlyShownInPage,
      resetPageFilter,
      searchSpecsString,
      productsCategoriesNames,
      showFilters,
      showResetFilters,
      result,
      loading,
      canonicalLink,
      alternate,
    };
  },
});
</script>

<style lang="scss">
.category-page {
  width: 100vw;
  max-width: 1920px;
  &__loading {
    min-height: 75vh;
    @include flex-center;
    &--info {
      min-height: 25vh;
    }
  }
  &__no-products {
    margin: 2rem auto;
    margin-top: 5.625rem;
    width: fit-content;
    text-align: center;
    &__reset-filters {
      margin: 0 auto;
    }
    &__heading {
      margin-top: 1rem;
    }
  }
}
</style>
